import { apiUrl } from "../api/endpoints";
import { useEffect, useState, useContext, Fragment, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { STATIC_ICONS, defaultIcon } from '../Constants';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import TerminalIcon from '@mui/icons-material/TerminalOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloudUpload from '@mui/icons-material/CloudUploadOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import CollapseContext from "../hooks/CollapseContext";
import useWindowDimensions from "../hooks/useWindowDimensions";
import axios from 'axios';
import './details.css';
import './edit.css';
import './EditDashboard.css';
import './DownloadReportShared.css';
import '../Components/modal.css';
import { useAuth } from '../hooks/AuthProvider';
import { EditEvent } from './EditDatastream';
import WebDashboard from './WebDashboard';
import { Tooltip } from '@mui/material';
import Spinner from '../Components/Spinner';
import { deleteWithIcon } from '../lib/actions';

import { LanguageContext } from '../context/LanguageContext';
import dashboardTranslations from '../languages/dashboard.json';
import webDashboardTranslations from '../languages/webDashboard.json';
import Alert from '../Components/Alert';
import AppDialog from '../Components/AppDialog';
import WebDashboardLayoutModeField from '../Components/WebDashboardLayoutModeField';
import DashboardTabsHeader from '../Components/DashboardTabsHeader';
import { getErrorMessage } from '../utils/errorUtils';
import { DividerMenuIcon, DividerMenuOpenedIcon, ExpandMenuIcon, SettingsIcon } from '../svg/TemplateIcons';
import { CustomSwitch, DeleteIcon, DuplicateIcon, VerticalDividerIcon } from '../svg/UtilsIcons';
import {
    createDefaultWebDashboardTab,
    getEffectiveFixedWidthValue,
    normalizeWebDashboardLayoutMode,
    normalizeWebDashboardTabs,
    WEB_DASHBOARD_LAYOUT_MODES,
} from '../utils/webDashboardLayout';
import { useAiReportTypes } from '../utils/aiReportTypes';
import WorkDaysHoursPicker from '../Components/WorkDaysHoursPicker';

const getTabPopupPosition = (rect, { width = 340, estimatedHeight = 600 } = {}) => {
    if (!rect || typeof window === 'undefined') {
        return { top: 12, left: width / 2 + 12 };
    }

    const margin = 12;
    const preferredTop = rect.bottom + 8;
    const maxTop = Math.max(margin, window.innerHeight - margin - estimatedHeight);
    const minLeft = margin + width / 2;
    const maxLeft = Math.max(minLeft, window.innerWidth - margin - width / 2);
    const preferredLeft = rect.left + rect.width / 2;

    return {
        top: Math.max(margin, Math.min(preferredTop, maxTop)),
        left: Math.max(minLeft, Math.min(preferredLeft, maxLeft)),
    };
};

export const DashboardSettings = ({ state, setState, mode, dashboard, setDashboard, previousIcon, setPreviousIcon }) => {

    const { permissions } = useAuth();
    const { lang } = useContext(LanguageContext);

    const canEditDashboard = permissions['edit_dashboard'];
    const t = dashboardTranslations[lang].EditDashboard.dashboardSettings;
    const isRtl = ['ar', 'ur'].includes(lang);
    const { options: aiReportTypeOptions, availability: aiReportAvailability } = useAiReportTypes();
    const showAiReportTypeField = aiReportAvailability.dashboards !== false;

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = (type, message) => {
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    };

    const saveFile = async (e) => {
        if (!canEditDashboard) {
            showAlert('error', 'You do not have permission to perform this action.');
            return;
        }
        if (!e.target.files || e.target.files.length === 0) return;

        const prefix = 'temp-' + dashboard.tid,
            file = e.target.files[0],
            nameParts = file.name.split('.'),
            suffix = nameParts[nameParts.length - 1],
            random = Date.now().toString(),
            fileName = `${prefix}-${random}.${suffix}`;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', fileName);
        formData.append('type', "icons");

        try {
            const res = await axios.post(apiUrl(`file/upload`), formData);
            showAlert('success', res.data.message || 'File uploaded successfully.');

            setPreviousIcon(dashboard.icon && dashboard.icon.length > 0 ? dashboard.icon : previousIcon);
            setDashboard({ ...dashboard, icon: fileName });

        } catch (err) {
            console.error("File upload failed:", err);
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
        }
    };

    if (mode === 'edit' && !canEditDashboard) {
        return (
            <div id='modal' className={`editdash-modal ${state ? 'is-open' : ''}`}
                onClick={() => setState(false)}>
                <div id='side-popup' className={`editdash-side-popup ${state ? 'is-open' : ''}`}
                    onClick={(e) => e.stopPropagation()}>
                    <div className='modal-header'>
                        <h2 className='editdash-modal-header-title'>Permission Denied</h2>
                    </div>
                    <div className='modal-content editdash-modal-content--pad'>
                        <p>You do not have the required permission to edit dashboards.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id='modal' className={`editdash-modal ${state ? 'is-open' : ''}`}
            onClick={() => setState(false)}>
            <Alert
                open={alertState.open}
                close={() => setAlertState({ ...alertState, open: false })}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />
            <div id='side-popup' className={`editdash-side-popup ${state ? 'is-open' : ''}`}
                onClick={(e) => e.stopPropagation()}>
                <div className='modal-header'>
                    <h2 className='editdash-modal-header-title'>
                        {t.settingsTitle}
                    </h2>
                </div>
                <div className='modal-content editdash-modal-content'>
                    {mode === 'edit' ? (
                        <Fragment>
                            <label className='editdash-label-uppercase'>{t.labelIcon}</label>
                            <div className='edit-form-item editdash-center'>
                                {dashboard.icon && dashboard.icon.length > 0 ? (
                                    <Fragment>
                                        <input type='file' accept='image/*' className='edit-logo-section' title='' onChange={saveFile} />
                                        <img src={STATIC_ICONS + dashboard.icon} alt='' onClick={() => {
                                            setPreviousIcon(dashboard.icon);
                                            setDashboard({ ...dashboard, icon: '' });
                                        }} />
                                        <DeleteIcon className='editdash-icon-size' />
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <input type='file' accept='image/*' className='edit-logo-section' title='' onChange={saveFile} />
                                        <CloudUpload className='editdash-upload-icon' />
                                        <div className='editdash-upload-text'>
                                            {t.uploadLogoText}
                                        </div>
                                        <div className='edit-helper editdash-upload-helper'>
                                            {t.uploadHelper}
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        </Fragment>
                    ) : (
                        <div className='modal-info editdash-modal-info'>
                            <InfoOutlined className='editdash-icon-size' />
                            <div className='editdash-info-col'>
                                <h4 className='editdash-info-title'>{t.viewModeTitle}</h4>
                                <p>{t.viewModeDesc}</p>
                            </div>
                        </div>
                    )}

                    <div className='edit-form-item editdash-field'>
                        <label className='editdash-field-label'>{t.labelName}</label>
                        {mode === 'edit' ? (
                            <input type='text' value={dashboard.name || ''} onChange={(e) => setDashboard({ ...dashboard, name: e.target.value })} maxLength={50} className='add-column editdash-input' />
                        ) : (
                            <div className='modal-info editdash-readonly-box'>
                                <span className={`editdash-readonly-text ${dashboard.name ? '' : 'is-empty'}`}>
                                    {dashboard.name || t.placeholderName}
                                </span>
                            </div>
                        )}
                        <span className='editdash-counter'>
                            {dashboard.name?.length} / 50
                        </span>
                    </div>

                    <div className='edit-form-item editdash-field'>
                        <label className='editdash-field-label'>{t.labelDescription}</label>
                        {mode === 'edit' ? (
                            <textarea value={dashboard.description || ''} onChange={(e) => setDashboard({ ...dashboard, description: e.target.value })} maxLength={128} rows={5} className='add-column editdash-textarea'></textarea>
                        ) : (
                            <div className='modal-info editdash-readonly-box editdash-readonly-box--textarea'>
                                <span className={`editdash-readonly-text ${dashboard.description ? '' : 'is-empty'}`}>
                                    {dashboard.description || t.placeholderDescription}
                                </span>
                            </div>
                        )}
                        <span className='edit-helper editdash-helper-counter'>
                            {dashboard.description?.length || 0} / 128
                        </span>
                    </div>
                    {showAiReportTypeField && <div className='edit-form-item editdash-field'>
                        <label className='editdash-field-label'>AI Report Type</label>
                        {mode === 'edit' ? (
                            <select
                                value={dashboard.type || ''}
                                onChange={(e) => setDashboard({ ...dashboard, type: e.target.value || null })}
                                className='add-column editdash-input'
                            >
                                <option value=''>Select AI report type</option>
                                {aiReportTypeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className='modal-info editdash-readonly-box'>
                                <span className={`editdash-readonly-text ${dashboard.type ? '' : 'is-empty'}`}>
                                    {aiReportTypeOptions.find((option) => option.value === dashboard.type)?.label || 'No AI report type'}
                                </span>
                            </div>
                        )}
                    </div>}
                    {mode === 'edit' && (
                        <div className='edit-form-item editdash-field'>
                            <WorkDaysHoursPicker
                                value={dashboard.work_days_hours || []}
                                onChange={(nextRows) =>
                                    setDashboard({ ...dashboard, work_days_hours: nextRows })
                                }
                                labels={{
                                    title: 'Work days and hours',
                                    sameShift: 'Use same shift start / shift end for all work days',
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export function checkTabPermission(userRole, allowedRoles) {
    const role = userRole?.toLowerCase();


    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
        return false;
    }

    return allowedRoles.map(r => r.toLowerCase()).includes(role);
}



const copyToClipboard = (value) => {
    if (!value) return;
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(value)).catch(() => {});
    }
};

const statusLabel = (d) => {
    const raw = (d?.status ?? d?.connection_status ?? d?.connectionStatus ?? d?.is_online ?? d?.isOnline ?? d?.online);
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'boolean') return raw ? 'Online' : 'Offline';
    if (typeof raw === 'number') return raw === 1 ? 'Online' : 'Offline';
    return 'Unknown';
};

const statusDot = (label) => {
    const v = String(label || '').toLowerCase();
    if (v.includes('online') || v.includes('connected')) return 'var(--td-status-online)';
    if (v.includes('offline') || v.includes('disconnected')) return 'var(--td-status-offline)';
    if (v.includes('error') || v.includes('fail')) return 'var(--td-status-error)';
    return 'var(--td-status-warn)';
};

const DashboardHomeCard = ({ title, subtitle, right, children }) => (
    <div className="td-card" style={{ overflow:'auto' }}>
        <div className="td-card__header">
            <div className="td-card__header-left">
                <div className="td-card__title-row">
                    <h5 className="td-card__title">{title}</h5>
                    {subtitle ? <span className="td-card__subtitle">{subtitle}</span> : null}
                </div>
            </div>
            {right}
        </div>

        <div className="td-card__spacer" />
        <div className="td-card__body">{children}</div>
    </div>
);

const DashboardHomeField = ({ label, value, action, multiline }) => (
    <section className="td-field">
        <div className="td-field__top">
            <span className="td-field__label">{label}</span>
            {action}
        </div>

        <div className={multiline ? "td-field__box td-field__box--multiline" : "td-field__box"}>
            <div
                className={
                    multiline
                        ? (value ? "td-field__value td-field__value--multiline" : "td-field__value td-field__value--multiline td-field__value--empty")
                        : (value ? "td-field__value" : "td-field__value td-field__value--empty")
                }
            >
                {value || '-'}
            </div>
        </div>
    </section>
);

const DashboardHomeDevicesPanel = ({ t, devices, navigate, isRtl }) => {
    const devicesCount = Array.isArray(devices) ? devices.length : 0;

    return (
        <DashboardHomeCard
            title={t?.tabs?.home || 'Home'}
            subtitle={`${devicesCount} ${t?.devicesLabel || 'Devices'}`}
            right={null}
        >
            <div className="td-devices">
                <div className="td-devices__header">
                    <div>{t?.deviceNameLabel || 'Device name'}</div>
                    <div style={{ textAlign: isRtl ? 'left' : 'right' }}>
                        {t?.statusLabel || 'Status'}
                    </div>
                </div>

                {devicesCount === 0 ? (
                    <div className="td-devices__empty">
                        {t?.noDevices || 'No devices found.'}
                    </div>
                ) : (
                    <div className="td-devices__list">
                        {(devices || []).map((d, idx) => {
                            const label = statusLabel(d);
                            const dot = statusDot(label);
                            return (
                                <button
                                    key={d?.id ?? idx}
                                    type="button"
                                    onClick={() => navigate(`/devices/details/${d?.id}`)}
                                    className={
                                        idx % 2 === 0
                                            ? 'td-device-row td-device-row--even'
                                            : 'td-device-row td-device-row--odd'
                                    }
                                >
                                    <div
                                        className="td-device-row__name"
                                        title={d?.name || d?.device_name || d?.label || ''}
                                    >
                                        {d?.name || d?.device_name || d?.label || `#${d?.id ?? idx + 1}`}
                                    </div>

                                    <div
                                        className="td-device-row__status"
                                        style={{ justifyContent: isRtl ? 'flex-start' : 'flex-end' }}
                                    >
                                        <span className="td-device-row__dot" style={{ backgroundColor: dot }} />
                                        <span className="td-device-row__label">{label}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </DashboardHomeCard>
    );
};

const DashboardHomeSettingsPanel = ({
    t,
    dashboard,
    setDashboard,
    edit,
    exampleMode,
    canEditDashboard,
    previousIcon,
    setPreviousIcon,
    showAlert,
}) => {
    const subtitle = [dashboard?.hardware, dashboard?.connectionType].filter(Boolean).join(', ');

    const setField = (key, value) => {
        setDashboard((prev) => ({ ...prev, [key]: value }));
    };

    const onUploadIcon = async (e) => {
        if (!canEditDashboard) {
            showAlert && showAlert('error', 'You do not have permission to perform this action.');
            return;
        }
        if (!e?.target?.files || e.target.files.length === 0) return;

        const file = e.target.files[0];

        try {
            // Keep previous icon for cleanup after Apply (same behavior as old popup)
            setPreviousIcon && setPreviousIcon(dashboard?.icon && dashboard.icon.length > 0 ? dashboard.icon : '');
            const prefix = `dash-${dashboard?.tid || dashboard?.id || 'icon'}`;
            const nameParts = String(file.name || 'icon.png').split('.');
            const suffix = nameParts[nameParts.length - 1] || 'png';
            const fileName = `${prefix}-${Date.now()}.${suffix}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('fileName', fileName);
            formData.append('type', 'icons');

            const res = await axios.post(apiUrl(`file/upload`), formData);
            showAlert && showAlert('success', res?.data?.message || 'File uploaded successfully.');
            setDashboard((prev) => ({ ...prev, icon: fileName }));
        } catch (err) {
            // upload handled here
            console.error('Icon upload failed:', err);
        } finally {
            // allow re-uploading same file
            try { e.target.value = ''; } catch (_) {}
        }
    };

    const onRemoveIcon = () => {
        if (!canEditDashboard) return;
        setPreviousIcon && setPreviousIcon(dashboard?.icon && dashboard.icon.length > 0 ? dashboard.icon : '');
        setDashboard((prev) => ({ ...prev, icon: '' }));
    };

    const title = t?.dashboardSettingsPlaceHolder || 'Dashboard settings';
    const { options: aiReportTypeOptions, availability: aiReportAvailability } = useAiReportTypes();
    const showAiReportTypeField = aiReportAvailability.dashboards !== false;

    return (
        <DashboardHomeCard
            title={title}
            subtitle={subtitle}
            right={null}
        >
            {edit?.mode === 'edit' && canEditDashboard ? (
                <div className="td-edit-grid">
                    <div>
                        <div className="td-edit-label">{t?.labelIcon || t?.dashboardSettings?.labelIcon || 'Icon'}</div>
                        <div className="editdash-icon-upload">
                            <input
                                type="file"
                                accept="image/*"
                                className="editdash-icon-upload__input"
                                onChange={onUploadIcon}
                                title=""
                            />
                            {dashboard?.icon && dashboard.icon.length > 0 ? (
                                <button type="button" className="editdash-icon-upload__btn" onClick={onRemoveIcon} title="Remove icon">
                                    <img
                                        src={STATIC_ICONS + dashboard.icon}
                                        alt=""
                                        className="editdash-icon-upload__img"
                                    />
                                    <DeleteIcon className="editdash-icon-upload__delete" />
                                </button>
                            ) : (
                                <div className="editdash-icon-upload__placeholder">
                                    <CloudUpload className="editdash-icon-upload__cloud" />
                                    <div className="editdash-icon-upload__text">{t?.uploadLogoText || 'Upload icon'}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {exampleMode ? (
                        <div>
                            <div className="td-edit-label">{t?.exampleNameLabel || 'Example Name'}</div>
                            <input
                                type="text"
                                maxLength={100}
                                value={dashboard?.example_name || ''}
                                onChange={(e) => setField('example_name', e.target.value)}
                                className="add-column td-input"
                            />
                        </div>
                    ) : null}

                    <div>
                        <div className="td-edit-label">{t?.detailsTitleName || t?.labelName || 'Name'}</div>
                        <input
                            type="text"
                            maxLength={50}
                            value={dashboard?.name || ''}
                            onChange={(e) => setField('name', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">{t?.dashboardIdLabel || 'Dashboard ID'}</div>
                        <div className="add-column td-input td-inline-readonly">
                            <span className="td-inline-readonly__text">{dashboard?.tid || '-'}</span>
                            {dashboard?.tid ? (
                                <button
                                    type="button"
                                    className="td-circle-btn td-circle-btn--sm"
                                    onClick={() => copyToClipboard(dashboard.tid)}
                                    title="Copy"
                                >
                                    <TerminalIcon />
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <div>
                        <div className="td-edit-label">{t?.hardwareLabel || 'Hardware'}</div>
                        <input
                            type="text"
                            maxLength={50}
                            value={dashboard?.hardware || ''}
                            onChange={(e) => setField('hardware', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">{t?.connectionTypeLabel || 'Connection type'}</div>
                        <input
                            type="text"
                            maxLength={50}
                            value={dashboard?.connectionType || ''}
                            onChange={(e) => setField('connectionType', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">{t?.hotspotPrefix || 'Hotspot prefix'}</div>
                        <input
                            type="text"
                            maxLength={50}
                            value={dashboard?.hotspotPrefix || ''}
                            onChange={(e) => setField('hotspotPrefix', e.target.value)}
                            className="add-column td-input"
                        />
                    </div>

                    <div>
                        <div className="td-edit-label">{t?.detailsTitleDescription || t?.labelDescription || 'Description'}</div>
                        <textarea
                            className="add-column textarea-large td-textarea"
                            maxLength={128}
                            rows={5}
                            value={dashboard?.description || ''}
                            onChange={(e) => setField('description', e.target.value)}
                            placeholder={t?.detailsTitleDescription || 'Description'}
                        />
                    </div>

                    {showAiReportTypeField && <div>
                        <div className="td-edit-label">AI Report Type</div>
                        <select
                            value={dashboard?.type || ''}
                            onChange={(e) => setField('type', e.target.value || null)}
                            className="add-column td-input"
                        >
                            <option value="">Select AI report type</option>
                            {aiReportTypeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>}
                </div>
            ) : (
                <div>
                    {exampleMode ? (
                        <DashboardHomeField
                            label={t?.exampleNameLabel || 'Example Name'}
                            value={dashboard?.example_name || '-'}
                            action={
                                dashboard?.example_name ? (
                                    <button type="button" onClick={() => copyToClipboard(dashboard.example_name)} className="td-circle-btn" title="Copy">
                                        <TerminalIcon />
                                    </button>
                                ) : null
                            }
                        />
                    ) : null}
                    <DashboardHomeField
                        label={t?.detailsTitleName || t?.labelName || 'Name'}
                        value={dashboard?.name || '-'}
                        action={
                            dashboard?.name ? (
                                <button type="button" onClick={() => copyToClipboard(dashboard.name)} className="td-circle-btn" title="Copy">
                                    <TerminalIcon />
                                </button>
                            ) : null
                        }
                    />
                    <DashboardHomeField
                        label={t?.dashboardIdLabel || 'Dashboard ID'}
                        value={dashboard?.tid || '-'}
                        action={
                            dashboard?.tid ? (
                                <button type="button" onClick={() => copyToClipboard(dashboard.tid)} className="td-circle-btn" title="Copy">
                                    <TerminalIcon />
                                </button>
                            ) : null
                        }
                    />
                    <DashboardHomeField label={t?.hardwareLabel || 'Hardware'} value={dashboard?.hardware || '-'} />
                    <DashboardHomeField label={t?.connectionTypeLabel || 'Connection type'} value={dashboard?.connectionType || '-'} />
                    <DashboardHomeField label={t?.hotspotPrefix || 'Hotspot prefix'} value={dashboard?.hotspotPrefix || '-'} />
                    <DashboardHomeField
                        label="AI Report Type"
                        value={aiReportTypeOptions.find((option) => option.value === dashboard?.type)?.label || '-'}
                    />
                    <DashboardHomeField label={t?.detailsTitleDescription || t?.labelDescription || 'Description'} value={dashboard?.description || '-'} multiline />
                </div>
            )}
        </DashboardHomeCard>
    );
};


export const EditDashboard = ({ exampleMode = false } = {}) => {

    const { lang } = useContext(LanguageContext);
    const dashboardPageT = dashboardTranslations[lang] || dashboardTranslations.en || {};
    const t = dashboardPageT.EditDashboard || {};
    const webDashboardT = webDashboardTranslations[lang]?.webDashboard || {};
    const isRtl = ['ar', 'ur'].includes(lang);

    const [initialDashboard, setInitialDashboard] = useState({});
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, permissions } = useAuth();
    const userRole = user.role?.toLowerCase();
    const dims = useWindowDimensions();
    const backPath = exampleMode ? '/configuration/dashboard-examples' : '/dashboards';
    const detailsBasePath = exampleMode ? '/configuration/dashboard-examples/details' : '/dashboards/details';
    const fetchUrl = exampleMode
        ? apiUrl(`dashboard/examples/get/${id}`)
        : apiUrl(`dashboard/get/${id}`);
    const updateUrl = exampleMode
        ? apiUrl(`dashboard/examples/update/${id}`)
        : apiUrl(`dashboard/update/${id}`);
    const createUrl = exampleMode
        ? apiUrl(`dashboard/examples/create`)
        : apiUrl(`dashboard/create`);
    const deleteType = exampleMode ? 'dashboard/examples' : 'dashboard';
    const allowCustomTabRoles = !exampleMode;
    const [loading, setLoading] = useState(true);
    const [previousIcon, setPreviousIcon] = useState('');
    const [edit, setEdit] = useState({
        edit: false,
        tempId: id,
        dataId: 0,
        mode: searchParams.get('mode') || 'view',
        editEvents: false,
        eventId: 0
    });
    const [tabsWidth, setTabsWidth] = useState(false);
    const [allRoles, setAllRoles] = useState([]);
    const [isHeaderActionsMenuOpen, setIsHeaderActionsMenuOpen] = useState(false);
    const [isDashboardExpanded, setIsDashboardExpanded] = useState(false);
    const [dashboardViewportHeight, setDashboardViewportHeight] = useState(null);
    const dashboardBodyRef = useRef(null);
    const dashboardPanelRef = useRef(null);
    const dashboardViewportRef = useRef(null);

    const [dashboard, setDashboard] = useState({
        id: 0,
        name: '',
        datastream: null, 
        webdashboard: null,
        mobdashboard: null,
        icon: null,
        organization_id: 0,
        organization: {
            id: 0,
            name:'',
            devices: []
        },
        loading: true
    });

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right',
    });

    const showAlert = useCallback((type, message) => {
    setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
}, [setAlertState, isRtl]);


    const resolverRef = useRef(null);
    const headerActionsMenuRef = useRef(null);
    const rolePopupRef = useRef(null);
    const [dialog, setDialog] = useState({
        open: false,
        type: 'alert',
        variant: 'info',
        icon: 'alert',
        title: '',
        message: '',
        confirmText: 'Ok',
        cancelText: 'Cancel',
        showCancel: false,
    });

    const closeDialog = useCallback((result = false) => {
        setDialog((d) => ({ ...d, open: false }));
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const alert = useCallback(
        (opts) =>
            new Promise((resolve) => {
                resolverRef.current = resolve;

                setDialog({
                    open: true,
                    type: opts?.type || 'alert',
                    variant: opts?.variant || 'info',
                    icon: opts?.icon || 'alert',
                    title: opts?.title || '',
                    message: opts?.message || '',
                    confirmText: opts?.confirmText || 'Ok',
                    cancelText: opts?.cancelText || 'Cancel',
                    showCancel: Boolean(opts?.showCancel),
                });
            }),
        []
    );

    const canEditDashboard = exampleMode
        ? permissions['edit_dashboard_examples']
        : permissions['edit_dashboard'];
    const canAddDashboard = permissions['add_new_dashoard'];
    const canDeleteDashboard = exampleMode
        ? permissions['delete_dashboard_examples']
        : permissions['delete_dashboards'];
    const canViewDashboard = exampleMode
        ? permissions['add_new_dashoard'] || permissions['edit_dashboard_examples'] || permissions['delete_dashboard_examples']
        : canEditDashboard;
    const isCompactHeaderActions = dims.width <= 1280;

    const tabs = useMemo(() => [
        {
            name: t.tabs.home,
            icon: <HomeIcon />
        },
        {
            name: t.tabs.webDashboard,
            icon: <DashboardIcon />
        },
    ], [t.tabs.home, t.tabs.webDashboard]);


    const [currentTab, setCurrentTab] = useState(() => {
        const tabFromUrl = parseInt(searchParams.get('tab'));
        return !isNaN(tabFromUrl) ? tabFromUrl : 0;
    });

    const [currentDashboard, setCurrentDashboard] = useState(() => {
        const subDashboardIdxFromUrl = parseInt(searchParams.get('subDashboardIdx'));
        return !isNaN(subDashboardIdxFromUrl) ? subDashboardIdxFromUrl : 0;
    });


    const [addDashboard, setAddDashboard] = useState({
        adding: false,
        dashboardName: 'Dashboard',
        dashboardRoles: ['super', 'owner'],
        dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
    });
    const [isCurrentDashboardFixedWidth, setIsCurrentDashboardFixedWidth] = useState(true);
    const [isFixedWidthToggleReady, setIsFixedWidthToggleReady] = useState(false);
    const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState(false);
    const [isMultiSelectToggleReady, setIsMultiSelectToggleReady] = useState(false);
    const fixedWidthToggleHandlerRef = useRef(null);
    const multiSelectToggleHandlerRef = useRef(null);
    const [editingTabRoleData, setEditingTabRoleData] = useState(null);
    const [rolePopupRect, setRolePopupRect] = useState(null);
    const addPopupRef = useRef(null);
    const [addPopupRect, setAddPopupRect] = useState(null);

    const registerFixedWidthToggleHandler = useCallback((handler) => {
        if (typeof handler === 'function') {
            if (fixedWidthToggleHandlerRef.current === handler) return;
            fixedWidthToggleHandlerRef.current = handler;
            setIsFixedWidthToggleReady((prev) => prev || true);
            return;
        }

        if (fixedWidthToggleHandlerRef.current === null) return;
        fixedWidthToggleHandlerRef.current = null;
        setIsFixedWidthToggleReady((prev) => prev && false);
    }, []);

    const handleFixedWidthDashboardToggle = useCallback((nextChecked) => {
        if (typeof fixedWidthToggleHandlerRef.current === 'function') {
            fixedWidthToggleHandlerRef.current(nextChecked);
        }
    }, []);

    const handleFixedWidthStateChange = useCallback((nextChecked) => {
        setIsCurrentDashboardFixedWidth((prev) => (prev === nextChecked ? prev : nextChecked));
    }, []);

    const registerMultiSelectToggleHandler = useCallback((handler) => {
        if (typeof handler === 'function') {
            if (multiSelectToggleHandlerRef.current === handler) return;
            multiSelectToggleHandlerRef.current = handler;
            setIsMultiSelectToggleReady((prev) => prev || true);
            return;
        }

        if (multiSelectToggleHandlerRef.current === null) return;
        multiSelectToggleHandlerRef.current = null;
        setIsMultiSelectToggleReady((prev) => prev && false);
    }, []);

    const handleMultiSelectDashboardToggle = useCallback((nextChecked) => {
        if (typeof multiSelectToggleHandlerRef.current === 'function') {
            multiSelectToggleHandlerRef.current(nextChecked);
        }
    }, []);

    const handleMultiSelectStateChange = useCallback((nextChecked) => {
        setIsMultiSelectEnabled((prev) => (prev === nextChecked ? prev : nextChecked));
    }, []);

    useEffect(() => {
        const currentWebDashboard = dashboard?.webdashboard?.[currentDashboard];
        const fallbackWebDashboard =
            tabs[currentTab]?.name === t.tabs.webDashboard && edit.mode === 'edit'
                ? createDefaultWebDashboardTab()
                : null;

        const nextFixedWidth = getEffectiveFixedWidthValue(currentWebDashboard || fallbackWebDashboard);
        setIsCurrentDashboardFixedWidth((prev) => (prev === nextFixedWidth ? prev : nextFixedWidth));
    }, [dashboard?.webdashboard, currentDashboard, currentTab, edit.mode, t.tabs.webDashboard]);

    useEffect(() => {
        if (edit.mode !== 'edit') {
            return;
        }

        if (!dashboard || !Array.isArray(dashboard.webdashboard) || dashboard.webdashboard.length > 0) {
            return;
        }

        setDashboard((prevDashboard) => {
            if (
                !prevDashboard ||
                !Array.isArray(prevDashboard.webdashboard) ||
                prevDashboard.webdashboard.length > 0
            ) {
                return prevDashboard;
            }

            return {
                ...prevDashboard,
                webdashboard: [createDefaultWebDashboardTab()],
            };
        });

        if (currentDashboard !== 0) {
            setCurrentDashboard(0);
        }

        setSearchParams(
            (prev) => {
                prev.set('subDashboardIdx', '0');
                return prev;
            },
            { replace: true }
        );
    }, [dashboard, edit.mode, currentDashboard, setSearchParams]);


    const fetchRoles = useCallback(async () => {
        if (!allowCustomTabRoles) {
            setAllRoles(['super', 'owner']);
            return;
        }

        try {
            const res = await axios.get(apiUrl(`roles`));
            const roleNames = res.data.data.map(role => role.name.toLowerCase()); 
            setAllRoles(roleNames);
        } catch (err) {
            console.error("Failed to fetch roles:", err);
            showAlert('error', 'Failed to load roles for access control.');
            setAllRoles(['user', 'staff', 'owner', 'super']); 
        }
    }, [allowCustomTabRoles, showAlert]);
    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    useEffect(() => {
        if (!isCompactHeaderActions) {
            setIsHeaderActionsMenuOpen(false);
        }
    }, [isCompactHeaderActions]);

    useEffect(() => {
        if (!isHeaderActionsMenuOpen) return undefined;

        const handlePointerDown = (event) => {
            if (headerActionsMenuRef.current?.contains(event.target)) {
                return;
            }

            setIsHeaderActionsMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [isHeaderActionsMenuOpen]);


    const handleEditTabRoleSelection = useCallback((role) => {
        setEditingTabRoleData(prev => {
            if (!prev) return prev;
            if (!allowCustomTabRoles) return prev;

            let newRoles = new Set(prev.roles);

            if (['super', 'owner'].includes(role)) {
                return prev;
            }

            if (newRoles.has(role)) {
                newRoles.delete(role);
            } else {
                newRoles.add(role);
            }

            newRoles.add('super');
            newRoles.add('owner');

            return { ...prev, roles: Array.from(newRoles) };
        });
    }, [allowCustomTabRoles]);

    const handleEditTabNameChange = useCallback((value) => {
        setEditingTabRoleData(prev => {
            if (!prev) return prev;
            return { ...prev, name: value };
        });
    }, []);

    const saveEditedTabRoles = () => {
        if (!editingTabRoleData) return;

        setDashboard(prevDashboard => {
            const updatedWebDashboard = [...(prevDashboard.webdashboard || [])];
            const current = updatedWebDashboard[editingTabRoleData.index];
            if (!current) return prevDashboard;

            const nextNameRaw = typeof editingTabRoleData.name === 'string' ? editingTabRoleData.name.trim() : '';
            const nextName = nextNameRaw.length > 0 ? nextNameRaw : current.name;

            updatedWebDashboard[editingTabRoleData.index] = {
                ...current,
                name: nextName,
                access_role: allowCustomTabRoles
                    ? editingTabRoleData.roles
                    : ['super', 'owner']
            };

            return { ...prevDashboard, webdashboard: updatedWebDashboard };
        });

        setEditingTabRoleData(null);
                    setRolePopupRect(null);
    };

    useEffect(() => {
        const onDocMouseDown = (e) => {
            const target = e.target;

            if (editingTabRoleData) {
                const clickedInsideEdit = rolePopupRef.current && rolePopupRef.current.contains(target);
                const clickedEditTrigger = target?.closest?.('.dashboard-tab-settings-btn');
                if (!clickedInsideEdit && !clickedEditTrigger) {
                    setEditingTabRoleData(null);
                    setRolePopupRect(null);
                }
            }

            if (addDashboard.adding) {
                const clickedInsideAdd = addPopupRef.current && addPopupRef.current.contains(target);
                const clickedAddTrigger = target?.closest?.('.add-dashboard-trigger');
                if (!clickedInsideAdd && !clickedAddTrigger) {
                    setAddDashboard({
                        adding: false,
                        dashboardName: 'Dashboard',
                        dashboardRoles: ['super', 'owner'],
                        dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
                    });
                    setAddPopupRect(null);
                }
            }
        };

        document.addEventListener('mousedown', onDocMouseDown, true);
        return () => document.removeEventListener('mousedown', onDocMouseDown, true);
    }, [editingTabRoleData, addDashboard.adding]);

    useEffect(() => {
        if (!editingTabRoleData && !addDashboard.adding) return;
        const onAnyScroll = (event) => {
            const target = event.target;
            const scrolledInsideEdit = rolePopupRef.current && rolePopupRef.current.contains(target);
            const scrolledInsideAdd = addPopupRef.current && addPopupRef.current.contains(target);

            if (scrolledInsideEdit || scrolledInsideAdd) {
                return;
            }

            if (editingTabRoleData) {
                setEditingTabRoleData(null);
                setRolePopupRect(null);
            }
            if (addDashboard.adding) {
                setAddDashboard({
                    adding: false,
                    dashboardName: 'Dashboard',
                    dashboardRoles: ['super', 'owner'],
                    dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
                });
                setAddPopupRect(null);
            }
        };
        window.addEventListener('scroll', onAnyScroll, true);
        window.addEventListener('resize', onAnyScroll);
        return () => {
            window.removeEventListener('scroll', onAnyScroll, true);
            window.removeEventListener('resize', onAnyScroll);
        };
    }, [editingTabRoleData, addDashboard.adding]);


    const handleRoleSelection = useCallback((role) => {
        setAddDashboard(prev => {
            if (!allowCustomTabRoles) return prev;
            let newRoles = new Set(prev.dashboardRoles);

            if (['super', 'owner'].includes(role)) {
                return prev;
            }

            if (newRoles.has(role)) {
                newRoles.delete(role);
            } else {
                newRoles.add(role);
            }

            newRoles.add('super');
            newRoles.add('owner');

            return { ...prev, dashboardRoles: Array.from(newRoles) };
        });
    }, [allowCustomTabRoles]);

    const isWebDashboardTabSelected = tabs[currentTab]?.name === t.tabs.webDashboard;
    const activeWebDashboard = useMemo(() => {
        if (!isWebDashboardTabSelected) {
            return null;
        }

        const currentWebDashboard = dashboard?.webdashboard?.[currentDashboard];
        if (currentWebDashboard) {
            return currentWebDashboard;
        }

        if (edit.mode !== 'edit') {
            return null;
        }

        return createDefaultWebDashboardTab();
    }, [
        currentDashboard,
        dashboard?.webdashboard,
        edit.mode,
        isWebDashboardTabSelected,
    ]);

    const isWebDashboardTabActive =
        isWebDashboardTabSelected &&
        Boolean(activeWebDashboard);
    const isDashboardExpandedActive = isWebDashboardTabActive && isDashboardExpanded;
    const effectiveTabsWidth = isDashboardExpandedActive ? false : tabsWidth;
    const updateDashboardViewportHeight = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const panelElement = dashboardBodyRef.current;
        const viewportElement = dashboardViewportRef.current;

        if (!panelElement || !viewportElement) {
            setDashboardViewportHeight(null);
            return;
        }

        const panelRect = panelElement.getBoundingClientRect();
        const viewportRect = viewportElement.getBoundingClientRect();
        const spacing = 8;
        const nextHeight = Math.floor(panelRect.bottom - viewportRect.top - spacing);

        if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
            setDashboardViewportHeight(null);
            return;
        }

        setDashboardViewportHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    }, []);

    useEffect(() => {
        if (!isWebDashboardTabActive) {
            setDashboardViewportHeight(null);
            setIsDashboardExpanded(false);
            return undefined;
        }

        if (typeof window === 'undefined') {
            return undefined;
        }

        let rafId = 0;
        const scheduleViewportMeasure = () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }

            rafId = window.requestAnimationFrame(() => {
                updateDashboardViewportHeight();
            });
        };

        scheduleViewportMeasure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', scheduleViewportMeasure);

            return () => {
                if (rafId) {
                    window.cancelAnimationFrame(rafId);
                }
                window.removeEventListener('resize', scheduleViewportMeasure);
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            scheduleViewportMeasure();
        });

        [dashboardBodyRef.current, dashboardViewportRef.current]
            .filter(Boolean)
            .forEach((element) => resizeObserver.observe(element));

        window.addEventListener('resize', scheduleViewportMeasure);

        return () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', scheduleViewportMeasure);
        };
    }, [
        isWebDashboardTabActive,
        currentDashboard,
        edit.mode,
        isDashboardExpandedActive,
        updateDashboardViewportHeight,
    ]);

    const open = useContext(CollapseContext),
        maxWidth = `${dims.width - (open ? tabsWidth ? 300 : 170 : tabsWidth ? 500 : 520)}px`,
        fallbackDashboardHeight = Math.max(280, dims.height - (isDashboardExpandedActive ? 166 : (edit.mode === 'edit' ? 332 : 300))),
        dashboardMaxHeight = `${dashboardViewportHeight || fallbackDashboardHeight}px`
    const dashboardExpandLabel = isDashboardExpandedActive
        ? (webDashboardT.collapseAction || t.collapseAction || 'Click to collapse')
        : (webDashboardT.expandAction || t.expandAction || 'Click to expand');


    function moveBorder(className, originalIndex) {
        const border = document.querySelector('.' + className + '-tab-border');
        const targetTab = document.querySelector(`.${className}-tab[data-original-index="${originalIndex}"]`);

        if (border && targetTab) {
            const dashboardWidth = document.defaultView.getComputedStyle(targetTab).width;
            border.style.width = dashboardWidth;
            border.style.left = targetTab.offsetLeft + 'px';
            targetTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(fetchUrl, exampleMode ? undefined : {
                params: { client_platform: 'web' }
            });
            const dashboardData = res.data.data;

            if (dashboardData && typeof dashboardData === 'object') {
                const filteredProcessedWebdashboard = normalizeWebDashboardTabs(dashboardData.webdashboard);
                const normalizedDashboardData = {
                    ...dashboardData,
                    organization: dashboardData.organization || {
                        id: 0,
                        name: '',
                        devices: [],
                    },
                };
                setDashboard({ ...normalizedDashboardData, webdashboard: filteredProcessedWebdashboard });
                setInitialDashboard({ ...normalizedDashboardData, webdashboard: filteredProcessedWebdashboard });

            } else {
                throw new Error("Received an invalid data format from the server.");
            }

        } catch (err) {
            const errorMessage = getErrorMessage(err);
            setAlertState({
                open: true,
                message: errorMessage,
                type: 'error',
            });
            setDashboard(null);

            if (err.response && err.response.status === 404) {
                setTimeout(() => navigate(backPath), 3000);
            }

        } finally {
            setLoading(false);
        }
    }, [backPath, exampleMode, fetchUrl, navigate]);

    useEffect(() => {
        if (!dashboard?.webdashboard || dashboard.webdashboard.length === 0) {
            if (currentDashboard !== 0) setCurrentDashboard(0);
            if (searchParams.get('subDashboardIdx') !== '0') setSearchParams(prev => { prev.set('subDashboardIdx', '0'); return prev; }, { replace: true });
            return;
        }

        const currentDashboardData = dashboard.webdashboard[currentDashboard];
        const isCurrentValid = currentDashboardData && checkTabPermission(userRole, currentDashboardData.access_role);

        let newDashboardIndex = currentDashboard;
        if (!isCurrentValid) {
            const firstViewableIndex = dashboard.webdashboard.findIndex((wd) =>
                checkTabPermission(userRole, wd.access_role)
            );
            newDashboardIndex = firstViewableIndex !== -1 ? firstViewableIndex : 0;
        }

        if (newDashboardIndex !== currentDashboard) {
            setCurrentDashboard(newDashboardIndex);
            setSearchParams(prev => { prev.set('subDashboardIdx', newDashboardIndex.toString()); return prev; }, { replace: true });
        } else {
            const subDashboardIdxFromUrl = parseInt(searchParams.get('subDashboardIdx'));
            if (isNaN(subDashboardIdxFromUrl) || subDashboardIdxFromUrl !== currentDashboard) {
                setSearchParams(prev => { prev.set('subDashboardIdx', currentDashboard.toString()); return prev; }, { replace: true });
            }
        }
    }, [dashboard?.webdashboard, userRole, currentDashboard, searchParams, setSearchParams]);


    useEffect(() => {
        if (tabs[currentTab].name === t.tabs.webDashboard) {
            setTimeout(() => {
                moveBorder('dashboard', currentDashboard);
            }, 0);
        }
    }, [currentDashboard, currentTab, tabs, t.tabs.webDashboard, dashboard]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const duplicateDashboard = async () => {
        if (!canAddDashboard) {
            showAlert('error', 'You do not have permission to perform this action.');
            return;
        }
        try {
            if (!initialDashboard || !initialDashboard.id) {
                showAlert('error', 'Cannot duplicate: Original dashboard data is missing.');
                return;
            }
            if (!exampleMode && !initialDashboard.organization_id) {
                showAlert('error', 'Cannot duplicate: The original dashboard is missing an organization.');
                return;
            }

            const {
                name,
                example_name,
                ...rest
            } = initialDashboard;
            const body = {
                ...rest,
                name: `${name || 'Dashboard'}-Copy`
            };

            delete body.id;
            delete body.dashboard_example_id;
            delete body.organization;
            delete body.loading;
            delete body.organization_name;

            if (exampleMode) {
                body.example_name = example_name
                    ? `${example_name}-Copy`
                    : `${name || 'Dashboard'}-Copy`;
                delete body.organization_id;
            }

            const res = await axios.post(
                createUrl,
                body,
                { headers: { "Content-Type": 'application/json' } }
            );

            const newDashboardId =
                res.data?.data?.id ??
                res.data?.data?.dashboard_example_id;

            showAlert(
                'success',
                exampleMode
                    ? (t.exampleDuplicateSuccess || 'Dashboard example created successfully.')
                    : (t.duplicateSuccess || 'Dashboard duplicated successfully!')
            );

            setTimeout(() => {
                navigate(`${detailsBasePath}/${newDashboardId}`);
            }, 2000);

        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
        }
    };

    function cloneDashboardForUpload(sourceDashboard) {
        if (!sourceDashboard || typeof sourceDashboard !== 'object') {
            return sourceDashboard;
        }

        const clonedDashboard = {
            ...sourceDashboard,
            webdashboard: (sourceDashboard.webdashboard || []).map((page) => ({
                ...page,
                widgets: (page.widgets || []).map((widget) => ({
                    ...widget,
                    extra_props: widget?.extra_props
                        ? { ...widget.extra_props }
                        : widget.extra_props,
                })),
            })),
        };

        delete clonedDashboard.id;
        delete clonedDashboard.dashboard_example_id;
        delete clonedDashboard.organization;
        delete clonedDashboard.loading;
        delete clonedDashboard.organization_name;

        if (exampleMode) {
            delete clonedDashboard.organization_id;
        }

        return clonedDashboard;
    }

    const updateDashboard = async (e) => {

        if (!canEditDashboard) {
            showAlert('error', 'You do not have permission to perform this action.');
            return;
        }

        if (e?.target?.classList?.contains('disabled') || loading) {
            return;
        }

        setLoading(true);

        try {
            const dashboardToUpload = cloneDashboardForUpload(dashboard);
            dashboardToUpload.client_platform = 'web';
            dashboardToUpload.webdashboard = dashboardToUpload.webdashboard || [];
            delete dashboardToUpload.mobdashboard;
            
            await uploadWidgetsFiles(dashboardToUpload); 

            const res = await axios.post(
                updateUrl,
                dashboardToUpload, 
                { headers: { "Content-Type": "application/json", "X-Client-Platform": "web" } }
            );

            if (previousIcon && previousIcon.length > 0) {
                try {
                    await axios.get(apiUrl(`file/delete/${previousIcon}`));
                } catch (deleteErr) {
                    console.warn('Non-critical error: Failed to delete previous icon.', deleteErr);
                }
            }

            showAlert(
                'success',
                res.data.message ||
                    (exampleMode
                        ? (t.exampleUpdateSuccess || 'Dashboard example updated successfully.')
                        : t.updateSuccess)
            );
            setEdit((prev) => ({ ...prev, mode: 'view' }));

            await fetchDashboard(); 

        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);

        } finally {
            setLoading(false);
        }
    };

    async function uploadWidgetsFiles(dashboardToProcess) {
        try {
            if (!dashboardToProcess || !dashboardToProcess.webdashboard) {
                return; 
            }

            for (const page of (dashboardToProcess.webdashboard || [])) {
                if (page.widgets) { 
                    for (const widget of page.widgets) {
                        if (widget.type === 'background-image') {
                            if (widget.extra_props?.file instanceof File) {
                                if (widget.extra_props?.imageUrl) {
                                    await deleteFile(widget.extra_props?.imageUrl);
                                }
                                widget.extra_props.imageUrl = await saveFile(
                                    widget.extra_props.file,
                                    'icons',
                                    `background-image`
                                );
                            }
                            if (widget.extra_props && 'file' in widget.extra_props) {
                                delete widget.extra_props.file;
                            }

                        }
                        else if (widget.type === 'image-button') {
                            if (widget.extra_props?.images) {
                                widget.extra_props.images = await Promise.all(
                                    (widget.extra_props.images || []).map(async (img) => {
                                        let imageUrl = img.imageUrl;
                                        if (img?.file instanceof File) {
                                            if (img.imageUrl) {
                                                await deleteFile(img.imageUrl);
                                            }
                                            imageUrl = await saveFile(
                                                img.file,
                                                'icons',
                                                'image-button'
                                            );
                                        }
                                        const { file, ...newObj } = img;
                                        return { ...newObj, imageUrl };
                                    })
                                );
                            }
                        }
                    }
                }
            }
        } catch (uploadError) {
            console.error("An error occurred during the widget file upload process.", uploadError);
            const errorMessage = getErrorMessage(uploadError);
            showAlert('error', `Failed to save changes due to a file error: ${errorMessage}`);
            throw uploadError;
        }
    }

    async function deleteFile(fileName, showAlertCallback) {
        const actualFileName = fileName.startsWith('/icons/') ? fileName.substring('/icons/'.length) : fileName;
        if (!actualFileName || typeof actualFileName !== 'string' || actualFileName.trim() === '') {
            if (showAlertCallback) showAlertCallback('error', 'Invalid filename provided.');
            console.error('deleteFile was called with an invalid filename.');
            return false;
        }

        try {
            const body = [`/icons/${actualFileName}`];
            const res = await axios.post(
                apiUrl(`file/delete`),
                body
            );

            if (showAlertCallback) {
                const messageType = res.status === 207 ? 'warning' : 'success';
                showAlertCallback(messageType, res.data.message || 'File deleted successfully.');
            }

            return true;

        } catch (err) {
            console.error("File deletion failed:", err);

            if (showAlertCallback) {
                const errorMessage = getErrorMessage(err);
                showAlertCallback('error', errorMessage);
            }

            throw err;
        }
    }

    async function saveFile(file, type = 'icons', prefix = 'file', showAlertCallback) {
        if (!(file instanceof File)) {
            console.error("saveFile was called without a valid File object.");
            if (showAlertCallback) showAlertCallback('error', 'Invalid file provided for upload.');
            return null;
        }

        const suffix = file.name.split('.').pop();
        const newFileName = `${prefix}-${Date.now()}.${suffix}`; 

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', newFileName);
        formData.append('type', type);

        try {
            const res = await axios.post(
                apiUrl(`file/upload`),
                formData
            );

            if (showAlertCallback) {
                showAlertCallback('success', res.data.message || 'File uploaded successfully.');
            }

            return newFileName; 

        } catch (err) {
            console.error("File upload failed:", err);

            if (showAlertCallback) {
                const errorMessage = getErrorMessage(err);
                showAlertCallback('error', errorMessage);
            }

            throw err;
        }
    }

    const handleCancelEdit = async () => {
        const ok = await alert({
            type: 'confirm',
            variant: 'info',
            icon: 'alert',
            title: t.confirmTitle || 'Confirm',
            message: t.confirmLeave,
            confirmText: t.confirm || 'Ok',
            cancelText: t.cancel || 'Cancel',
            showCancel: true,
        });
        if (!ok) return;

        const newIconWasUploaded = dashboard.icon &&
            dashboard.icon.length > 0 &&
            dashboard.icon !== initialDashboard.icon;

        if (newIconWasUploaded) {
            try {
                const body = ['/icons/' + dashboard.icon];
                await axios.post(apiUrl(`file/delete`), body);
                console.log("Orphaned icon cleanup successful:", dashboard.icon);
            } catch (err) {
                console.warn("Non-critical error: Failed to clean up orphaned icon.", err);
            }
        }

        setEdit((prev) => ({ ...prev, mode: 'view' }));
        setDashboard(initialDashboard);
    };
    if (!canViewDashboard) {
        return (
            <div className='configuration '>
                <div className='config-header'>
                    <h2>Permission Denied</h2>
                </div>
                <div className='editdash-permission-pad'>
                    <p>
                        {exampleMode
                            ? 'You do not have the required permission to edit dashboard examples.'
                            : 'You do not have the required permission to edit dashboard.'}
                    </p>
                </div>
            </div>
        );
    }


    return (
        loading ? <Spinner /> :
            dashboard?.id ?
                <Fragment>
                    <Alert
                        open={alertState.open}
                        close={() => setAlertState({ ...alertState, open: false })}
                        message={alertState.message}
                        type={alertState.type}
                        position={alertState.position}
                    />

                    <AppDialog
                        open={dialog.open}
                        type={dialog.type}
                        variant={dialog.variant}
                        icon={dialog.icon}
                        title={dialog.title}
                        message={dialog.message}
                        confirmText={dialog.confirmText}
                        cancelText={dialog.cancelText}
                        showCancel={dialog.showCancel}
                        onConfirm={() => closeDialog(true)}
                        onCancel={() => closeDialog(false)}
                        onClose={() => closeDialog(false)}
                    />

                    <div className='details-container' dir={isRtl ? 'rtl' : 'ltr'}>
                        {edit.editEvents && permissions['edit_templates'] && <EditEvent mode={edit.mode}
                            close={() => setEdit(prev => ({ ...prev, editEvents: false }))}
                            event={dashboard.events.find(ev => ev.id === edit.eventId)}
                            setEvent={(newEvent) => setDashboard(prev => ({
                                ...prev, events: prev.events.map(ev => ev.id === newEvent.id ? newEvent : ev)
                            }))} />}

                       <div className="details-control-panel">
    <div className="details-control-panel-inside" />

    <div className="details-control-panel-glow" />

    <div className="details-control-panel-content">
        <div
            className={`details-control-btns ${
                effectiveTabsWidth ? 'details-control-btns-row' : 'details-control-btns-column'
            }`}
        >
            <button
                type="button"
                className="details-control-btn details-control-btn-ghost"
                onClick={() => navigate(backPath)}
            >
                <small>{'\u2715'}</small>
            </button>

            <button
                type="button"
                className="details-control-btn details-control-btn-ghost"
                onClick={() => setTabsWidth(prev => !prev)}
            >
                <ExpandMenuIcon />
            </button>
        </div>

        <div
            className={
                'details-control-divider ' +
                (effectiveTabsWidth
                    ? 'details-control-divider-expanded'
                    : 'details-control-divider-collapsed')
            }
        >
            {effectiveTabsWidth ? <DividerMenuOpenedIcon /> : <DividerMenuIcon />}
        </div>

        <div
            className={
                'details-control-tabs details-control-tabs-rail ' +
                (effectiveTabsWidth
                    ? 'details-control-tabs-expanded'
                    : 'details-control-tabs-collapsed')
            }
        >
            {tabs.map((tab, idx) => {
                const isActive = idx === currentTab;

                return (
                    <Tooltip title={tab.name} placement="right" key={idx}>
                        <div
                            className={
                                'details-control-tab details-control-tab-rail ' +
                                (isActive ? 'active ' : '') +
                                (effectiveTabsWidth
                                    ? 'details-control-tab-rail-expanded'
                                    : 'details-control-tab-rail-collapsed')
                            }
                            onClick={() => {
                                setCurrentTab(idx);
                                setSearchParams(
                                    prev => {
                                        prev.set('tab', idx.toString());

                                        if (tab.name === t.tabs.webDashboard) {
                                            if (!prev.has('subDashboardIdx')) {
                                                prev.set(
                                                    'subDashboardIdx',
                                                    currentDashboard.toString(),
                                                );
                                            }
                                        } else {
                                            prev.delete('subDashboardIdx');
                                        }

                                        return prev;
                                    },
                                    { replace: true },
                                );
                                setEditingTabRoleData(null);
                    setRolePopupRect(null);
                            }}
                        >
                            {isActive && (
                                <div className="details-control-tab-cyan-bar" />
                            )}

                            <span
                                className={
                                    'details-control-tab-icon ' +
                                    (effectiveTabsWidth
                                        ? 'details-control-tab-icon-expanded'
                                        : '')
                                }
                            >
                                {tab.icon || '•'}
                            </span>

                            {effectiveTabsWidth && (
                                <span className="details-control-tab-label">
                                    {tab.name}
                                </span>
                            )}
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    </div>
</div>


                        <div className={`details-content${isWebDashboardTabActive ? ' editdash-details-content-dashboard' : ''}${isDashboardExpandedActive ? ' dashboard-edit-expanded-content' : ''}`}>
                            <div className={`details-head${isDashboardExpandedActive ? ' dashboard-edit-expanded-head' : ''}`}>
    <img
        alt="icon"
        src={`${STATIC_ICONS}${dashboard.icon?.length > 0 ? dashboard.icon : defaultIcon}`}
    />

    <div className="details-remain-head details-remain-head--full">
        <div
            className="details-remain-head-part editdash-head-row"
        >
            <div className="details-head-left">
                <h1>{exampleMode ? (dashboard.example_name || dashboard.name) : dashboard.name}</h1>

                {!isCompactHeaderActions && (
                <div className="details-inline-actions">
                    {canAddDashboard && (
                        <button
                            type="button"
                            className="details-inline-action-btn"
                            onClick={duplicateDashboard}
                        >
                            <DuplicateIcon />
                        </button>
                    )}

                    <span className="details-inline-divider">
                        <VerticalDividerIcon />
                    </span>

                    {canDeleteDashboard ? (
                        <button
                            type="button"
                            className="details-inline-action-btn details-inline-delete-btn"
                            onClick={() =>
                                deleteWithIcon(
                                    dashboard.id,
                                    dashboard.icon,
                                    deleteType,
                                    () => navigate(backPath),
                                    alert
                                )
                            }
                        >
                            <DeleteIcon />
                        </button>
                    ) : null}
                </div>
                )}
            </div>

            <div className="details-head-right">
                {isCompactHeaderActions ? (
                    <div className="details-head-action-group" ref={headerActionsMenuRef}>
                        <button
                            type="button"
                            className="details-inline-action-btn details-head-more-btn"
                            onClick={() => setIsHeaderActionsMenuOpen((prev) => !prev)}
                            aria-label={t.moreActions || 'More actions'}
                            aria-expanded={isHeaderActionsMenuOpen}
                        >
                            <MoreHorizIcon />
                        </button>

                        {isHeaderActionsMenuOpen ? (
                            <div className="details-actions-menu details-head-actions-menu open">
                                {canAddDashboard && (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            duplicateDashboard();
                                        }}
                                    >
                                        <DuplicateIcon />
                                        <span>{t.duplicate || 'Duplicate'}</span>
                                    </button>
                                )}

                                {canDeleteDashboard ? (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item danger"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            deleteWithIcon(
                                                dashboard.id,
                                                dashboard.icon,
                                                deleteType,
                                                () => navigate(backPath),
                                                alert,
                                            );
                                        }}
                                    >
                                        <DeleteIcon />
                                        <span>{t.delete || 'Delete'}</span>
                                    </button>
                                ) : null}

                                <div className="details-actions-menu-sep" />

                                {edit.mode === 'edit' ? (
                                    <Fragment>
                                        <button
                                            type="button"
                                            className="details-actions-menu-item"
                                            onClick={() => {
                                                setIsHeaderActionsMenuOpen(false);
                                                handleCancelEdit();
                                            }}
                                        >
                                            <span />
                                            <span>{t.buttonCancel || 'Cancel'}</span>
                                        </button>

                                        <button
                                            type="button"
                                            className="details-actions-menu-item"
                                            disabled={
                                                JSON.stringify(dashboard) === JSON.stringify(initialDashboard)
                                            }
                                            onClick={() => {
                                                if (JSON.stringify(dashboard) === JSON.stringify(initialDashboard)) {
                                                    return;
                                                }
                                                setIsHeaderActionsMenuOpen(false);
                                                updateDashboard();
                                            }}
                                        >
                                            <span />
                                            <span>{t.buttonApply || 'Apply'}</span>
                                        </button>
                                    </Fragment>
                                ) : canEditDashboard ? (
                                    <button
                                        type="button"
                                        className="details-actions-menu-item"
                                        onClick={() => {
                                            setIsHeaderActionsMenuOpen(false);
                                            setEdit((prev) => ({ ...prev, mode: 'edit' }));
                                        }}
                                    >
                                        <span />
                                        <span>{t.editButton || 'Edit'}</span>
                                    </button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : edit.mode === 'edit' ? (
                    <div className='editdash-head-edit-actions edit-mode-action-bar'>
                        <button
                            type="button"
                            className="btn btn-cancel-details editdash-clickable edit-mode-action-btn edit-mode-action-btn--cancel"
                            onClick={handleCancelEdit}
                        >
                            {t.buttonCancel}
                        </button>

                        <button
                            type="button"
                            className={`btn asset-edit-main-btn edit-btn-save edit-mode-action-btn edit-mode-action-btn--apply ${
                                JSON.stringify(dashboard) === JSON.stringify(initialDashboard)
                                    ? 'disabled'
                                    : ''
                            }`}
                            onClick={updateDashboard}
                        >
                            {t.buttonApply}
                        </button>
                    </div>
                ) : canEditDashboard ? (
                    <button
                        className="edit-btn editdash-edit-btn"
                        onClick={() =>
                            setEdit(prev => ({ ...prev, mode: 'edit' }))
                        }
                    >
                        {t.editButton}
                    </button>
                ) : null}
            </div>
        </div>
    </div>
</div>

                            <div
                                ref={dashboardBodyRef}
                                className={`details-body editdash-details-body-col${isDashboardExpandedActive ? ' dashboard-edit-expanded-body' : ''}`}
                            >
                                <div className={`details-tab-content${isWebDashboardTabActive ? ' editdash-details-tab-content-dashboard' : ''}`}>
                                    {
                                        (isWebDashboardTabActive ?
                                            <div ref={dashboardPanelRef} className={`editdash-dashboard-panel${isDashboardExpandedActive ? ' dashboard-edit-expanded-panel' : ''}`}>
                                                <DashboardTabsHeader
                                                    className={`template-dashboard-tabs-header${isDashboardExpandedActive ? ' dashboard-edit-expanded-tabs-header' : ''}`}
                                                    tabsClassName="dashboard-tabs editdash-dashboard-tabs template-dashboard-tabs-scroll"
                                                    onDrop={(e) => {
                                                        const dragIdx = parseInt(e.dataTransfer.getData('text/plain'));
                                                        const targetTabElement = e.target.closest('.dashboard-tab');
                                                        if (!targetTabElement) return;

                                                        const dropIdx = parseInt(targetTabElement.dataset.originalIndex);

                                                        if (dragIdx >= 0 && dropIdx >= 0 && dragIdx !== dropIdx) {
                                                            setDashboard(prevDashboard => {
                                                                const wdb = [...(prevDashboard.webdashboard || [])];
                                                                [wdb[dragIdx], wdb[dropIdx]] = [wdb[dropIdx], wdb[dragIdx]];
                                                                return { ...prevDashboard, webdashboard: wdb };
                                                            });

                                                            setCurrentDashboard(dropIdx);
                                                            setSearchParams(
                                                                prev => {
                                                                    prev.set('subDashboardIdx', dropIdx.toString());
                                                                    return prev;
                                                                },
                                                                { replace: true }
                                                            );
                                                        }
                                                    }}
                                                    onDragOver={(e) => e.preventDefault()}
                                                    actions={(
                                                        <>
                                                            {edit.mode === 'edit' ? (
                                                                <div className='template-fixed-width-toggle'>
                                                                    <span className='template-fixed-width-toggle-label'>
                                                                        {webDashboardT.multiSelectWidgets || 'Multi Select'}
                                                                    </span>
                                                                    <CustomSwitch
                                                                        checked={isMultiSelectEnabled}
                                                                        onChange={handleMultiSelectDashboardToggle}
                                                                        disabled={!isMultiSelectToggleReady}
                                                                    />
                                                                    <span className='template-fixed-width-toggle-label'>
                                                                        {webDashboardT.fixedWidthDashboard || 'Fixed Width Dashboard'}
                                                                    </span>
                                                                    <CustomSwitch
                                                                        checked={isCurrentDashboardFixedWidth}
                                                                        onChange={handleFixedWidthDashboardToggle}
                                                                        disabled={!isFixedWidthToggleReady}
                                                                    />
                                                                </div>
                                                            ) : null}
                                                            <div className='dashboard-edit-expand-actions'>
                                                                <button
                                                                    type='button'
                                                                    className={`dashboard-edit-expand-toggle${isDashboardExpandedActive ? ' active' : ''}`}
                                                                    onClick={() => setIsDashboardExpanded(prev => !prev)}
                                                                    title={dashboardExpandLabel}
                                                                    aria-label={dashboardExpandLabel}
                                                                >
                                                                    {isDashboardExpandedActive ? (
                                                                        <CloseFullscreenIcon className='dashboard-edit-expand-toggle-icon' />
                                                                    ) : (
                                                                        <OpenInFullIcon className='dashboard-edit-expand-toggle-icon' />
                                                                    )}
                                                                    <span className='dashboard-edit-expand-toggle-label'>
                                                                        {dashboardExpandLabel}
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                >
                                                    {dashboard.webdashboard?.map((wd, idx) => {
                                                        const allowedRoles = wd.access_role || wd.access_roles;
                                                        const canView = checkTabPermission(userRole, allowedRoles);
                                                        if (!canView) return null;

                                                        const isActive = idx === currentDashboard;
                                                        const isPopupOpen = editingTabRoleData && editingTabRoleData.index === idx;

                                                        return (
                                                            <Tooltip
                                                                title={edit.mode === 'edit' ? t.dragToChangeOrder : ''}
                                                                key={wd.id || idx}
                                                                disableInteractive
                                                                PopperProps={{ disablePortal: true }}
                                                            >
                                                                <div
                                                                    className={`dashboard-tab editdash-dashboard-tab ${isActive ? 'active' : ''}`}
                                                                    draggable={edit.mode === 'edit'}
                                                                    data-original-index={idx}
                                                                    title={wd.name}
                                                                    onDragStart={(e) => {
                                                                        if (
                                                                            e.target.closest('.settings-btn') ||
                                                                            e.target.closest('.role-editor-popup')
                                                                        ) {
                                                                            e.preventDefault();
                                                                            return;
                                                                        }
                                                                        e.dataTransfer.setData('text/plain', idx.toString());
                                                                    }}
                                                                    onClick={() => {
                                                                        if (isPopupOpen) return;

                                                                        setCurrentDashboard(idx);
                                                                        setSearchParams(
                                                                            prev => {
                                                                                prev.set('subDashboardIdx', idx.toString());
                                                                                return prev;
                                                                            },
                                                                            { replace: true }
                                                                        );
                                                                        setEditingTabRoleData(null);
                    setRolePopupRect(null);
                                                                    }}

                                                                >
                                                                    <span className='editdash-pointer-none'>{wd.name}</span>

                                                                    {edit.mode === 'edit' && (
                                                                        <button
                                                                            type="button"
                                                                            className="settings-btn dashboard-tab-settings-btn editdash-settings-btn"
                                                                            onMouseDown={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                try {
                                                                                    const r = e.currentTarget.getBoundingClientRect();
                                                                                    setRolePopupRect({
                                                                                        ...getTabPopupPosition(r, { width: 320, estimatedHeight: 560 }),
                                                                                        width: Math.max(240, r.width)
                                                                                    });
                                                                                } catch (_) {}

                                                                                setEditingTabRoleData(prev => {
                                                                                    if (prev?.index === idx) return null;
                                                                                    return {
                                                                                        index: idx,
                                                                                        name: wd?.name || '',
                                                                                        roles: Array.isArray(wd.access_role)
                                                                                            ? [...wd.access_role]
                                                                                            : Array.isArray(wd.access_roles)
                                                                                            ? [...wd.access_roles]
                                                                                            : [],
                                                                                        layoutMode: normalizeWebDashboardLayoutMode(wd?.layoutMode),
                                                                                    };
                                                                                });
                                                                            }}
                                                                            aria-label="Edit dashboard roles"
                                                                        >
                                                                            <SettingsIcon className='editdash-settings-icon' />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </Tooltip>
                                                        );
                                                    })}

                                                    <div className="dashboard-tab-border"></div>

                                                    {edit.mode === 'edit' ? (
                                                        <Tooltip title={t.dashboardAddTab || 'Add Tab'}>
                                                            <div className='editdash-add-tab-wrap'>
                                                                <span
                                                                    className="details-control-btn add-dashboard-trigger editdash-add-tab-trigger"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            const r = e.currentTarget.getBoundingClientRect();
                                                                            setAddPopupRect({
                                                                                ...getTabPopupPosition(r, { width: 320, estimatedHeight: 620 }),
                                                                                width: Math.max(240, r.width)
                                                                            });
                                                                        } catch (_) {}
                                                                        setAddDashboard(prev => ({ ...prev, adding: true }));
                                                                    }}
                                                                >
                                                                    +
                                                                </span>
                                                            </div>
                                                        </Tooltip>
                                                    ) : null}

                                                </DashboardTabsHeader>


                                                {editingTabRoleData && rolePopupRect
                                                    ? createPortal(
                                                          <div
                                                              className="editdash-portal-layer"
                                                              onMouseDown={(e) => e.stopPropagation()}
                                                              onClick={(e) => e.stopPropagation()}
                                                          >
                                                              <div
                                                                  ref={rolePopupRef}
                                                                  className="role-editor-popup editdash-role-popup editdash-role-popup--portal"
                                                                  style={{
                                                                      position: 'fixed',
                                                                      top: rolePopupRect.top,
                                                                      left: rolePopupRect.left,
                                                                      zIndex: 100000,
                                                                      width: 320,
                                                                      maxWidth: 'calc(100vw - 24px)',
                                                                      maxHeight: 'calc(100vh - 24px)',
                                                                      overflowY: 'auto',
                                                                      overflowX: 'hidden',
                                                                  }}
                                                                  onMouseDown={(e) => e.stopPropagation()}
                                                                  onClick={(e) => e.stopPropagation()}
                                                              >
                                                                  <h4 className="editdash-role-popup-title">
                                                                      {t.editRolesTitle || 'Edit roles'}
                                                                  </h4>

                                                                  <div className="editdash-role-popup-section">
                                                                      <div className="editdash-role-popup-section-label">
                                                                          {t.tabName || 'Tab name'}
                                                                                                                                                {dashboard.webdashboard?.length > 0 ? (
                                                                          <Tooltip title={t.tabDelete || 'Delete'}>
                                                                              <button
                                                                                  type="button"
                                                                                  className="details-delete-btn"
                                                                                  style={{ position: 'absolute', flexShrink: 0, marginRight: '8px', right:'2%' }}
                                                                                  onClick={async (e) => {
                                                                                      e.stopPropagation();
                                                                                      const targetIndex = editingTabRoleData?.index;
                                                                                      if (targetIndex == null || targetIndex < 0) return;

                                                                                      const ok = await alert({
                                                                                          type: 'confirm',
                                                                                          variant: 'danger',
                                                                                          icon: 'delete',
                                                                                          title: t.deleteTitle || 'Delete',
                                                                                          message: t.deleteConfirm,
                                                                                          confirmText: t.delete || 'Delete',
                                                                                          cancelText: t.cancel || 'Cancel',
                                                                                          showCancel: true,
                                                                                      });

                                                                                      if (!ok) return;

                                                                                      const totalTabs = dashboard.webdashboard?.length || 0;
                                                                                      let nextIndex = currentDashboard;
                                                                                      if (totalTabs <= 1) {
                                                                                          nextIndex = 0;
                                                                                      } else if (targetIndex < currentDashboard) {
                                                                                          nextIndex = Math.max(0, currentDashboard - 1);
                                                                                      } else if (targetIndex === currentDashboard) {
                                                                                          nextIndex = currentDashboard > 0 ? currentDashboard - 1 : 0;
                                                                                      }

                                                                                      setDashboard(prevDashboard => {
                                                                                          const newWebdashboards = (prevDashboard.webdashboard || []).filter(
                                                                                              (_, index) => index !== targetIndex
                                                                                          );
                                                                                          return {
                                                                                              ...prevDashboard,
                                                                                              webdashboard: newWebdashboards
                                                                                          };
                                                                                      });

                                                                                      setEditingTabRoleData(null);
                                                                                      setRolePopupRect(null);

                                                                                      setCurrentDashboard(nextIndex);
                                                                                      setSearchParams(
                                                                                          prev => {
                                                                                              prev.set('subDashboardIdx', nextIndex.toString());
                                                                                              return prev;
                                                                                          },
                                                                                          { replace: true }
                                                                                      );
                                                                                  }}
                                                                              >
                                                                                  <DeleteIcon color={'var(--root-background)'} />
                                                                              </button>
                                                                          </Tooltip>
                                                                      ) : null}
                                                                      </div>

                                                                      <input
                                                                          type="text"
                                                                          className="add-column editdash-input editdash-role-popup-input"
                                                                          maxLength={50}
                                                                          value={editingTabRoleData.name || ''}
                                                                          onChange={(e) =>
                                                                              handleEditTabNameChange(e.target.value)
                                                                          }
                                                                      />
                                                                  </div>

                                                                  <div className="editdash-role-popup-section">
                                                                      <WebDashboardLayoutModeField
                                                                          value={editingTabRoleData.layoutMode}
                                                                          title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                                                                          helperText={
                                                                              webDashboardT.layoutModeReadonlyHelper ||
                                                                              'This layout mode is locked after the tab is created.'
                                                                          }
                                                                          translations={webDashboardT}
                                                                          readOnly
                                                                          compact
                                                                      />
                                                                  </div>

                                                                  {allowCustomTabRoles ? (
                                                                      <div className="multi-select-container editdash-multi-select editdash-multi-select--edit">
                                                                          {allRoles
                                                                              .filter((role) => !['super', 'owner'].includes(role))
                                                                              .map((role) => (
                                                                                  <div
                                                                                      key={role}
                                                                                      onClick={(e) => {
                                                                                          e.stopPropagation();
                                                                                          handleEditTabRoleSelection(role);
                                                                                      }}
                                                                                      className="editdash-role-item"
                                                                                  >
                                                                                      <input
                                                                                          type="checkbox"
                                                                                          readOnly
                                                                                          checked={editingTabRoleData.roles.includes(role)}
                                                                                          className="editdash-role-checkbox"
                                                                                      />
                                                                                      {role}
                                                                                  </div>
                                                                              ))}
                                                                      </div>
                                                                  ) : (
                                                                      <div className="editdash-role-popup-section">
                                                                          <div className="td-field__box">
                                                                              <div className="td-field__value">
                                                                                  {t.fixedExampleRolesHelper || 'Access is fixed to Super / Owner for dashboard examples.'}
                                                                              </div>
                                                                          </div>
                                                                      </div>
                                                                  )}

                                                                  <div
                                                                      className="editdash-role-popup-actions"
                                                                      style={{ justifyContent: 'space-between', alignItems: 'center' }}
                                                                  >
                                                                      
                                                                      <div style={{ display: 'flex', gap: '8px' }}>
                                                                          <button
                                                                              type="button"
                                                                              className="btn btn-cancel editdash-role-popup-btn"
                                                                              onClick={(e) => {
                                                                                  e.stopPropagation();
                                                                                  setEditingTabRoleData(null);
                                                                                  setRolePopupRect(null);
                                                                              }}
                                                                          >
                                                                              {t.cancel || 'Cancel'}
                                                                          </button>

                                                                          <button
                                                                              type="button"
                                                                              className="btn edit-btn-save-role editdash-role-popup-btn"
                                                                              onClick={(e) => {
                                                                                  e.stopPropagation();
                                                                                  saveEditedTabRoles();
                                                                                  setRolePopupRect(null);
                                                                              }}
                                                                          >
                                                                              {t.apply || 'Apply'}
                                                                          </button>
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                          </div>,
                                                          document.body
                                                      )
                                                    : null}

                                                {addDashboard.adding && addPopupRect
                                                    ? createPortal(
                                                          <div
                                                              className="editdash-portal-layer"
                                                              onMouseDown={(e) => e.stopPropagation()}
                                                              onClick={(e) => e.stopPropagation()}
                                                          >
                                                              <div
                                                                  ref={addPopupRef}
                                                                  className="role-editor-popup role-editor-popup--add editdash-role-popup editdash-role-popup--add editdash-role-popup--portal"
                                                                  style={{
                                                                      position: 'fixed',
                                                                      top: addPopupRect.top,
                                                                      left: addPopupRect.left,
                                                                      zIndex: 100000,
                                                                      width: 320,
                                                                      maxWidth: 'calc(100vw - 24px)',
                                                                  }}
                                                                  onMouseDown={(e) => e.stopPropagation()}
                                                                  onClick={(e) => e.stopPropagation()}
                                                              >
                                                                  <h4 className="editdash-role-popup-title">
                                                                      {t.addTabTitle || t.dashboardAddTab || 'Add Tab'}
                                                                  </h4>

                                                                  <div className="editdash-role-popup-section">
                                                                      <div className="editdash-role-popup-section-label">
                                                                          {t.tabName || 'Tab name'}
                                                                      </div>
                                                                      <input
                                                                          type="text"
                                                                          maxLength={50}
                                                                          value={addDashboard.dashboardName || ''}
                                                                          onChange={(e) =>
                                                                              setAddDashboard((prev) => ({
                                                                                  ...prev,
                                                                                  dashboardName: e.target.value,
                                                                              }))
                                                                          }
                                                                          className="add-column editdash-input editdash-role-popup-input"
                                                                      />
                                                                  </div>

                                                                  <div className="editdash-role-popup-section">
                                                                      <WebDashboardLayoutModeField
                                                                          value={addDashboard.dashboardLayoutMode}
                                                                          onChange={(nextMode) =>
                                                                              setAddDashboard((prev) => ({
                                                                                  ...prev,
                                                                                  dashboardLayoutMode: nextMode,
                                                                              }))
                                                                          }
                                                                          title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                                                                          helperText={
                                                                              webDashboardT.layoutModeHelper ||
                                                                              'Choose how widget sizes and positions are stored for this tab.'
                                                                          }
                                                                          translations={webDashboardT}
                                                                          compact
                                                                      />
                                                                  </div>

                                                                  {allowCustomTabRoles ? (
                                                                      <div className="multi-select-container editdash-multi-select editdash-multi-select--add">
                                                                          {allRoles
                                                                              .filter((role) => !['super', 'owner'].includes(role))
                                                                              .map((role) => (
                                                                                  <div
                                                                                      key={role}
                                                                                      onClick={(e) => {
                                                                                          e.stopPropagation();
                                                                                          handleRoleSelection(role);
                                                                                      }}
                                                                                      className="editdash-role-item"
                                                                                  >
                                                                                      <input
                                                                                          type="checkbox"
                                                                                          readOnly
                                                                                          checked={addDashboard.dashboardRoles.includes(role)}
                                                                                          className="editdash-role-checkbox"
                                                                                      />
                                                                                      {role}
                                                                                  </div>
                                                                              ))}
                                                                      </div>
                                                                  ) : (
                                                                      <div className="editdash-role-popup-section">
                                                                          <div className="td-field__box">
                                                                              <div className="td-field__value">
                                                                                  {t.fixedExampleRolesHelper || 'Access is fixed to Super / Owner for dashboard examples.'}
                                                                              </div>
                                                                          </div>
                                                                      </div>
                                                                  )}

                                                                  <div className="editdash-role-popup-actions">
                                                                      <button
                                                                          type="button"
                                                                          className="btn btn-cancel editdash-role-popup-btn"
                                                                          onClick={(e) => {
                                                                              e.stopPropagation();
                                                                              setAddDashboard({
                                                                                  adding: false,
                                                                                  dashboardName: 'Dashboard',
                                                                                  dashboardRoles: ['super', 'owner'],
                                                                                  dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
                                                                              });
                                                                              setAddPopupRect(null);
                                                                          }}
                                                                      >
                                                                          {t.cancel || 'Cancel'}
                                                                      </button>

                                                                      <button
                                                                          type="button"
                                                                          className="btn edit-btn-save editdash-role-popup-btn"
                                                                          onClick={(e) => {
                                                                              e.stopPropagation();

                                                                              const nextNameRaw =
                                                                                  typeof addDashboard.dashboardName === 'string'
                                                                                      ? addDashboard.dashboardName.trim()
                                                                                      : '';
                                                                              const nextName = nextNameRaw.length > 0 ? nextNameRaw : 'Dashboard';

                                                                              const newDashboardEntry = createDefaultWebDashboardTab({
                                                                                  id: `new-${Date.now()}`,
                                                                                  name: nextName,
                                                                                  accessRole: allowCustomTabRoles
                                                                                      ? addDashboard.dashboardRoles
                                                                                      : ['super', 'owner'],
                                                                                  layoutMode: addDashboard.dashboardLayoutMode,
                                                                              });

                                                                              setDashboard((prevDashboard) => ({
                                                                                  ...prevDashboard,
                                                                                  webdashboard: [...(prevDashboard.webdashboard || []), newDashboardEntry],
                                                                              }));

                                                                              const newIndex = dashboard.webdashboard?.length || 0;
                                                                              setCurrentDashboard(newIndex);
                                                                              setSearchParams(
                                                                                  (prev) => {
                                                                                      prev.set('subDashboardIdx', newIndex.toString());
                                                                                      return prev;
                                                                                  },
                                                                                  { replace: true }
                                                                              );

                                                                              setAddDashboard({
                                                                                  adding: false,
                                                                                  dashboardName: 'Dashboard',
                                                                                  dashboardRoles: ['super', 'owner'],
                                                                                  dashboardLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
                                                                              });
                                                                              setAddPopupRect(null);
                                                                          }}
                                                                      >
                                                                          {t.add || 'Add'}
                                                                      </button>
                                                                  </div>
                                                              </div>
                                                          </div>,
                                                          document.body
                                                      )
                                                    : null}


                                                <div ref={dashboardViewportRef} className='editdash-dashboard-viewport'>
                                                    <WebDashboard key={`web-dashboard-${currentDashboard}-${activeWebDashboard?.id || 'default'}`} mode={edit.mode} devices={dashboard.organization.devices}
                                                        maxWidth='100%' maxHeight={dashboardMaxHeight}
                                                        externalFixedWidthControl={edit.mode === 'edit'}
                                                        onFixedWidthModeChange={handleFixedWidthStateChange}
                                                        onFixedWidthToggleHandlerReady={registerFixedWidthToggleHandler}
                                                        externalMultiSelectControl={edit.mode === 'edit'}
                                                        onMultiSelectStateChange={handleMultiSelectStateChange}
                                                        onMultiSelectToggleHandlerReady={registerMultiSelectToggleHandler}
                                                        dashboard={activeWebDashboard}
                                                        setDashboard={(webDashboard) => {
                                                            const targetIndex = dashboard?.webdashboard?.[currentDashboard] ? currentDashboard : 0;

                                                            setDashboard(prev => {
                                                                const updatedWebdashboards = [...(prev.webdashboard || [])];
                                                                updatedWebdashboards[targetIndex] = webDashboard;
                                                                return {
                                                                    ...prev,
                                                                    webdashboard: updatedWebdashboards
                                                                };
                                                            });

                                                            if (targetIndex !== currentDashboard) {
                                                                setCurrentDashboard(targetIndex);
                                                                setSearchParams(
                                                                    prev => {
                                                                        prev.set('subDashboardIdx', targetIndex.toString());
                                                                        return prev;
                                                                    },
                                                                    { replace: true }
                                                                );
                                                            }
                                                        }} />
                                                </div>
                                            </div> :
                                            (tabs[currentTab].name === t.tabs.home ?
<div className="td-home-page editdash-home-page editdash-home-page--single">
    <div className="editdash-home-single">
        <DashboardHomeSettingsPanel
            t={t}
            dashboard={dashboard}
            setDashboard={setDashboard}
            edit={edit}
            exampleMode={exampleMode}
            canEditDashboard={canEditDashboard}
            previousIcon={previousIcon}
            setPreviousIcon={setPreviousIcon}
            showAlert={showAlert}
        />
    </div>
</div>
: <div className='editdash-center-fill'>{tabs[currentTab].name} {t.tab}</div>
                                            ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </Fragment> : <div className='editdash-center-fill'>{dashboardTranslations[lang].noData}</div>
    );
};
